import React from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  ButtonVariant,
  ButtonSize,
} from '@vimily/bonjoro-ui-components-v2/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import qs from 'query-string';

import { assetUrl } from '~/helpers';
import CustomDomainModal from '../../Modals/CustomDomainModal';
import CustomDomainAddOnPaymentModal, {
  openCustomDomainAddOnPaymentModal,
} from '../../Modals/CustomDomainAddOnPaymentModal';
import { addOnsPricesApi } from '~/App/Modules/Billing/api';
import * as Ph from '~/App/Components/Placeholder';
import { BillingCycle, Service } from '~/types/models';
import { AddOnType } from '~/types/models/addon';
import {
  CHECKOUT_STARTED,
  CUSTOM_DOMAIN_LEARN_MORE,
  CUSTOM_DOMAIN_LEARN_MORE_GET,
  GET_CUSTOM_DOMAIN,
  GET_CUSTOM_DOMAIN_CONTINUE,
} from '~/generated/AnalyticsTypes';

import useAnalytics from '~/react-hooks/use-analytics';
import { showSignupToUpgradeModal } from '~/App/Components/Modals/UpgradeModals/helpers';
import { CheckoutType } from '~/App/Modules/NewBilling/Pages/Checkout';
import { useFeatures } from '~/react-hooks/use-features';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { AddOnIcons, AddOnIntervalLabel } from '~/App/Modules/NewBilling/utils';
import { UpgradeModalViewContext } from '~/App/Services/BillingAnalytics';
import { billingRoutes } from '~/App/Modules/NewBilling';

export enum CustomDomainModalUid {
  AddOn = 'custom-domain-add-on',
  LearnMore = 'custom-domain-learn-more',
}

const hideCustomDomainAddOnModal = () => {
  window.dispatchEvent(new Event(`${CustomDomainModalUid.AddOn}::close`));
};

export const hideCustomDomainLearnMoreModal = () => {
  window.dispatchEvent(new Event(`${CustomDomainModalUid.LearnMore}::close`));
};

const customDomainIcon =
  AddOnIcons[AddOnType.CustomDomain][Service.VideoMessaging];

const LandingDomainAddOn = () => {
  const { canSwitchPlan } = useFeatures();

  const { billing_cycle: interval } = useCurrentTeam();
  const { has_valid_card_on_file: hasValidCardOnFile } = useCurrentTeam();
  const analytics = useAnalytics();

  const handleLearnMoreClicked = () => {
    analytics.track(CUSTOM_DOMAIN_LEARN_MORE);
    window.dispatchEvent(new Event(`${CustomDomainModalUid.LearnMore}::open`));
  };

  const getCustomDomainAddOnPrice = async () => {
    const response = await addOnsPricesApi().getAll();
    const addOn = response.data.find(
      (item) => item.type === AddOnType.CustomDomain
    );

    if (addOn) {
      // filter out any grandfathered addons
      const addOns = addOn.addons.filter((addon) => addon.is_active);

      const monthly = addOns.find(
        (item) => item.interval === BillingCycle.Monthly
      );

      const yearly = addOns.find(
        (item) => item.interval === BillingCycle.Yearly
      );

      if (monthly && yearly) {
        const amountPerMonth = `$${monthly.amount}`;
        const amountPerYear = `$${yearly.amount}`;
        const amountToPay =
          interval === BillingCycle.Yearly ? yearly.amount : monthly.amount;

        const billingInfo = `Billed ${amountToPay} USD ${AddOnIntervalLabel[interval]}`;

        return {
          amountPerMonth,
          amountPerYear,
          amountToPay,
          billingInfo,
        };
      }
    }

    return null;
  };

  const { data, isLoading } = useQuery(
    'get-add-ons-price',
    getCustomDomainAddOnPrice
  );

  const handlePurchaseCustomDomainAddOn = (event: string) => () => {
    analytics.track(event);

    hideCustomDomainAddOnModal();
    hideCustomDomainLearnMoreModal();

    const context = UpgradeModalViewContext.CustomDomainAddon;
    analytics.track(CHECKOUT_STARTED, { context });

    if (!hasValidCardOnFile) {
      window.location.href = `${billingRoutes.checkout}${
        CheckoutType.AddOns
      }/?${qs.stringify({
        items: AddOnType.CustomDomain,
        redirect: window.location.pathname,
        context,
      })}`;
    } else {
      openCustomDomainAddOnPaymentModal();
    }
  };

  const handleCustomDomainAddOnClicked = () => {
    if (!canSwitchPlan) {
      showSignupToUpgradeModal();
      return;
    }
    analytics.track(GET_CUSTOM_DOMAIN);
    window.dispatchEvent(new Event(`${CustomDomainModalUid.AddOn}::open`));
  };

  if (isLoading || data == null) {
    return (
      <Ph.Item>
        <Ph.Row style={{ display: 'block' }}>
          <Ph.Col size={1} style={{ height: '164px' }} />
        </Ph.Row>
      </Ph.Item>
    );
  }

  const modalCopy = `Custom domain add-on is ${
    data.amountPerMonth
  } USD per month${
    interval === BillingCycle.Monthly
      ? '.'
      : `, billed at ${data.amountPerYear} USD per year.`
  } We will be adding the charge to your saved payment method.`;

  return (
    <>
      <div className='border border-solid border-neutral-300 rounded-md p-5 mt-5 max-w-sm'>
        <div className='flex items-center gap-x-3 mb-3'>
          <img src={customDomainIcon} width='20' height='20' />
          <h4 className='v2-font-circular text-base v2-text-neutral-800 m-0'>
            Custom domain Add-on
          </h4>
        </div>
        {canSwitchPlan && (
          <h3 className='text-lg text-neutral-700 mt-0 mb-5 ml-0 md:ml-8'>
            {data?.amountPerMonth}/mo
            <span className='text-xs text-neutral-400 font-normal ml-2'>
              {data?.billingInfo}
            </span>
          </h3>
        )}
        <div className='flex flex-col gap-y-3 md:flex-row md:gap-x-4'>
          <Button
            className='w-full md:w-auto py-2 text-sm'
            variant={ButtonVariant.Primary}
            size={ButtonSize.Small}
            onClick={handleCustomDomainAddOnClicked}
          >
            Get Custom domain add-on
          </Button>
          <Button
            className='w-full md:w-auto py-2 text-sm'
            variant={ButtonVariant.Secondary}
            size={ButtonSize.Small}
            onClick={handleLearnMoreClicked}
          >
            Learn more
          </Button>
        </div>
      </div>

      <CustomDomainModal
        uid={CustomDomainModalUid.AddOn}
        className='!w-[438px]'
      >
        <div className='flex flex-col gap-y-7 items-center'>
          <img src={customDomainIcon} width='50' height='50' />
          <h4 className='text-xl text-neutral-700 my-0'>
            Custom domain add-on
          </h4>
          <p className='text-sm text-neutral-600 text-center leading-5 mb-0'>
            {modalCopy}
          </p>
          <div className='flex flex-col gap-y-3 md:flex-row md:gap-x-4'>
            <Button
              className='w-full md:w-auto py-2'
              variant={ButtonVariant.Secondary}
              onClick={hideCustomDomainAddOnModal}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              className='w-full md:w-auto py-2'
              onClick={handlePurchaseCustomDomainAddOn(
                GET_CUSTOM_DOMAIN_CONTINUE
              )}
            >
              Continue
            </Button>
          </div>
        </div>
      </CustomDomainModal>

      <CustomDomainModal uid={CustomDomainModalUid.LearnMore} showCloseButton>
        <div className='flex flex-col flex-1 gap-y-7 items-center'>
          <h4 className='text-xl text-neutral-700 mx-auto mt-0 my-0'>
            Custom domain add-on
          </h4>
          <img
            className='max-w-full'
            src={assetUrl('assets/images/add-ons/custom-domain.png')}
          />
          <div>
            <p className='text-sm text-neutral-600 text-center leading-5 mb-5'>
              Host your Bonjoro videos on a unique URL that matches your company
              domain:
            </p>
            <ul className='list-none pl-0 mb-0'>
              <li className='mb-3'>
                <FontAwesomeIcon
                  className='mr-3 text-turquoise-700'
                  icon={faCheck}
                />
                Add extra brand recognition to the videos you send with Bonjoro
              </li>
              <li className='mb-3'>
                <FontAwesomeIcon
                  className='mr-3 text-turquoise-700'
                  icon={faCheck}
                />
                Get more control of your email inboxing and improved
                deliverability
              </li>
              <li>
                <FontAwesomeIcon
                  className='mr-3 text-turquoise-700'
                  icon={faCheck}
                />
                Add a professional touch to your team&apos;s customer
                communications
              </li>
            </ul>
          </div>
          <Button
            className='w-full md:w-auto py-2'
            variant={ButtonVariant.Primary}
            onClick={handlePurchaseCustomDomainAddOn(
              CUSTOM_DOMAIN_LEARN_MORE_GET
            )}
          >
            Get custom domain
          </Button>
        </div>
      </CustomDomainModal>

      <CustomDomainAddOnPaymentModal amount={data.amountToPay} />
    </>
  );
};

export default LandingDomainAddOn;
